import React from 'react';
import cx from 'classnames';
import dayjs from 'dayjs';
import T from 'i18n-react';

import cypressIds from 'Components/cypress_ids.json';
import MobileBreak from 'Components/shared/MobileBreak/MobileBreak';
import texts from 'Components/texts.json';
import type { SaleTimes } from 'Types/saleTimes';
import { getNextSaleDay } from 'Utilities/dateHelpers/dateHelpers';
import { applyCypressData } from 'Utilities/helpers';

import { HOUR_AM_PM, HOUR_MINUTES_AM_PM } from '../saleTimeConsts';

import Time from './Time';

import styles from './SaleTimeMessage.module.scss';

T.setTexts(texts);

interface SaleTimeMessageProps {
	isSaleActive: SaleTimes['isSaleActive'];
	isSaleLaterToday: SaleTimes['isSaleLaterToday'];
	millisecondsRemaining: SaleTimes['millisecondsRemaining'];
	nextSaleStartsAt: SaleTimes['nextSaleStartsAt'];
}

const SaleTimeMessage: React.FC<SaleTimeMessageProps> = ({
	isSaleActive = false,
	isSaleLaterToday,
	millisecondsRemaining,
	nextSaleStartsAt,
}) => {
	const nextSaleDay = getNextSaleDay({ nextSaleStartsAt });
	const isCurrentlyOutOfSaleAndDayOfNextSale = Boolean(!isSaleActive && isSaleLaterToday);

	const isNextSaleTomorrowOrAfter = !isSaleActive && !isCurrentlyOutOfSaleAndDayOfNextSale;
	const getSaleTimeText = (name: string, options: unknown) => T.translate(`vehicleList.saleTime.${name}`, options);

	if (!millisecondsRemaining) {
		return <div className={styles.noSaleTime} />;
	}

	const isNextSaleTomorrow = dayjs().add(1, 'day').day() === nextSaleDay;
	const nextSaleStartDate = dayjs(nextSaleStartsAt);
	const nextSaleFormat = nextSaleStartDate.minute() ? HOUR_MINUTES_AM_PM : HOUR_AM_PM;

	return (
		<div {...applyCypressData(cypressIds.text.saleTime)} className={cx(styles.saleTimeMessage)}>
			{isSaleActive &&
				getSaleTimeText('sale', {
					time: <Time colour="red" millisecondsRemaining={millisecondsRemaining} />,
				})}
			{isCurrentlyOutOfSaleAndDayOfNextSale &&
				getSaleTimeText('todaySale', {
					time: <Time colour="green" millisecondsRemaining={millisecondsRemaining} />,
				})}
			{isNextSaleTomorrowOrAfter &&
				getSaleTimeText('futureSale', {
					dayOfWeek: T.translate(`vehicleList.saleTime.${isNextSaleTomorrow ? 'tomorrowSale' : nextSaleDay}`),
					mobileBreak: <MobileBreak lineBreakCount={1} />,
					time: nextSaleStartDate.format(nextSaleFormat),
				})}
		</div>
	);
};

export default SaleTimeMessage;
